import { defineVariants } from "@/runtime/style-engine/define-variants";

/**
 * Root `<e-chat>` container.
 */
export const chatClasses = defineVariants({
  base: `flex flex-col h-full w-full`,
  variants: {},
});

/**
 * Scrollable inner container for both array and slot mode.
 */
export const chatScrollClasses = defineVariants({
  base: `flex-1 overflow-y-auto overflow-x-hidden py-4`,
  variants: {},
});

/**
 * Message group row — aligns avatar + content based on side.
 * Sets CSS custom properties that bubble children inherit across shadow boundaries.
 */
export const chatGroupClasses = defineVariants({
  base: `flex gap-2 px-4 py-1`,
  variants: {
    side: {
      left: `flex-row items-start`,
      right: `flex-row-reverse items-start`,
    },
  },
  defaults: {
    side: "left",
  },
});

/**
 * Content column within a group (meta + bubbles + status).
 */
export const chatGroupContentClasses = defineVariants({
  base: `flex flex-col gap-0.5 max-w-[75%]`,
  variants: {
    side: {
      left: `items-start`,
      right: `items-end`,
    },
  },
  defaults: {
    side: "left",
  },
});

/**
 * Meta line (sender name + time) above the bubbles.
 */
export const chatGroupMetaClasses = defineVariants({
  base: `flex items-center gap-2 text-xs px-1 text-(--color-foreground)/50`,
  variants: {},
});

/**
 * Individual message bubble.
 * Reads --chat-bubble-bg and --chat-bubble-opacity from the parent group.
 */
export const chatBubbleClasses = defineVariants({
  base: `
    px-3 py-2
    rounded-lg
    text-sm
    break-words
    text-(--color-foreground)
    flex flex-col gap-2
  `,
  variants: {
    side: {
      left: `bg-(--color-elevation-200)/50`,
      right: `bg-(--chat-user-color)/15`,
    },
  },
  defaults: {
    side: "left",
  },
});

/**
 * Inline image attachment inside a bubble.
 */
export const chatAttachmentImageClasses = defineVariants({
  base: `rounded-lg max-w-full max-h-64 object-cover cursor-pointer`,
  variants: {},
});

/**
 * File attachment card inside a bubble.
 */
export const chatAttachmentFileClasses = defineVariants({
  base: `
    flex items-center gap-3
    px-3 py-2
    rounded-lg
    bg-(--color-foreground)/5
    text-sm
    cursor-pointer
    hover:bg-(--color-foreground)/10
    transition-colors
  `,
  variants: {},
});

/**
 * File extension badge inside a file card.
 */
export const chatAttachmentFileExtClasses = defineVariants({
  base: `
    flex-shrink-0
    w-10 h-10
    rounded-lg
    flex items-center justify-center
    bg-(--color-primary)/15
    text-xs font-bold uppercase
    text-(--color-primary)
  `,
  variants: {},
});

/**
 * Link preview (OG card) inside a bubble.
 */
export const chatAttachmentLinkClasses = defineVariants({
  base: `
    block
    rounded-lg
    overflow-hidden
    bg-(--color-foreground)/5
    hover:bg-(--color-foreground)/10
    transition-colors
    no-underline
    text-(--color-foreground)
    cursor-pointer
  `,
  variants: {},
});

/**
 * Link preview thumbnail image.
 */
export const chatAttachmentLinkImageClasses = defineVariants({
  base: `w-full h-32 object-cover`,
  variants: {},
});

/**
 * Link preview text body (title + description + domain).
 */
export const chatAttachmentLinkBodyClasses = defineVariants({
  base: `px-3 py-2 flex flex-col gap-0.5`,
  variants: {},
});

/**
 * Blinking cursor shown at the end of a streaming bubble.
 * Uses opacity animation (GPU-compositable per performance rules).
 */
export const chatStreamingCursorClasses = defineVariants({
  base: `inline-block w-0.5 h-[1em] ml-0.5 align-text-bottom bg-(--color-foreground)/70 animate-[chat-cursor_0.8s_steps(2)_infinite]`,
  variants: {},
});

/**
 * Container for the three-dot typing indicator shown while the assistant is
 * generating its first token (streaming === true && msg === "").
 */
export const chatTypingIndicatorClasses = defineVariants({
  base: `inline-flex gap-1 items-center py-1`,
  variants: {},
});

/**
 * A single dot inside the typing indicator. Stagger via inline animation-delay
 * on dots 2 and 3 (0.15s, 0.3s).
 */
export const chatTypingDotClasses = defineVariants({
  base: `inline-block w-1.5 h-1.5 rounded-full bg-(--color-foreground)/50 animate-[chat-typing_1.2s_ease-in-out_infinite]`,
  variants: {},
});

/**
 * Avatar circle (first letter of sender name or image).
 */
export const chatAvatarClasses = defineVariants({
  base: `
    flex-shrink-0
    w-8 h-8
    rounded-full
    flex items-center justify-center
    text-xs font-medium
    bg-(--color-elevation-200)
    text-(--color-foreground)/70
    overflow-hidden
  `,
  variants: {},
});

/**
 * Date/time divider between message groups.
 */
export const chatDividerClasses = defineVariants({
  base: `
    flex items-center gap-3 px-4 py-2
    text-xs text-(--color-foreground)/40
    before:flex-1 before:h-px before:bg-(--color-foreground)/10
    after:flex-1 after:h-px after:bg-(--color-foreground)/10
  `,
  variants: {},
});

/**
 * Delivery status indicator below the last bubble in a group.
 */
export const chatStatusClasses = defineVariants({
  base: `text-[10px] px-1`,
  variants: {
    status: {
      pending: `text-(--color-foreground)/30`,
      sent: `text-(--color-foreground)/40`,
      received: `text-(--color-foreground)/50`,
      read: `text-(--color-primary)`,
      failed: `text-(--color-error)`,
    },
  },
  defaults: {
    status: "sent",
  },
});
